import React, { useEffect, useState } from "react";

const API_BASE_URL = "";

const emptyPackage = {
  tabLabel: "",
  title: "",
  shortDesc: "",
  image: "",
  imageAlt: "",
  duration: "",
  capacity: "",
  includes: [],
  whyLove: "",
  basePrice: "",
};

const getMediaUrl = (path) => {
  if (!path) {
    return "";
  }

  if (path.startsWith("http")) {
    return path;
  }

  if (path.startsWith("/uploads/")) {
    return `${API_BASE_URL}${path}`;
  }

  return path;
};

const clonePackage = (pkg) => ({
  ...emptyPackage,
  ...pkg,
  includes: Array.isArray(pkg?.includes) ? [...pkg.includes] : [],
});

const PackagesCms = () => {
  const [data, setData] = useState({
    subtitle: "",
    mainTitle: "",
    description: "",
    packages: [],
  });
  const [form, setForm] = useState(clonePackage(emptyPackage));
  const [editorIndex, setEditorIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem("indiaboatsAdminToken");
  const isEditing = editorIndex !== null;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const packagesResponse = await fetch(`${API_BASE_URL}/api/packages`);

        if (!packagesResponse.ok) {
          throw new Error("Failed to load Packages");
        }

        const packagesResult = await packagesResponse.json();

        setData({
          subtitle: packagesResult.subtitle || "",
          mainTitle: packagesResult.mainTitle || "",
          description: packagesResult.description || "",
          packages: packagesResult.packages || [],
        });
      } catch (error) {
        console.error("Packages fetch error:", error);
        alert("Failed to load packages data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSectionChange = (field, value) => {
    setData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Image upload failed");
      }

      handleFormChange("image", result.fileUrl);
    } catch (error) {
      console.error("Image upload error:", error);
      alert(error.message || "Failed to upload image");
    }
  };

  const startAdd = () => {
    setEditorIndex(null);
    setForm(clonePackage(emptyPackage));
  };

  const startEdit = (index) => {
    setEditorIndex(index);
    setForm(clonePackage(data.packages[index]));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFormToPackages = (packages) => {
    const nextPackages = [...packages];

    if (isEditing) {
      nextPackages[editorIndex] = clonePackage(form);
      return nextPackages;
    }

    const hasContent = Boolean(
      form.title ||
        form.tabLabel ||
        form.image ||
        form.shortDesc ||
        form.basePrice
    );

    if (hasContent) {
      nextPackages.push(clonePackage(form));
    }

    return nextPackages;
  };

  const persist = async (nextData, successMessage) => {
    const response = await fetch(`${API_BASE_URL}/api/packages`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(nextData),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to save changes");
    }

    const saved = {
      subtitle: result.data.subtitle || "",
      mainTitle: result.data.mainTitle || "",
      description: result.data.description || "",
      packages: result.data.packages || [],
    };

    setData(saved);
    alert(successMessage);
    return saved;
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const nextData = {
        ...data,
        packages: applyFormToPackages(data.packages),
      };

      const saved = await persist(nextData, "Packages updated successfully");

      if (!isEditing) {
        const addedNew = saved.packages.length > data.packages.length;
        if (addedNew) {
          startAdd();
        }
      } else {
        setForm(clonePackage(saved.packages[editorIndex] || emptyPackage));
      }
    } catch (error) {
      console.error("Save error:", error);
      alert(error.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (index) => {
    const item = data.packages[index];
    const label = item?.title || `Package ${index + 1}`;

    if (
      !window.confirm(
        `Delete "${label}"? This will remove it from the website.`
      )
    ) {
      return;
    }

    try {
      setSaving(true);

      const nextPackages = data.packages.filter(
        (_, itemIndex) => itemIndex !== index
      );

      await persist(
        { ...data, packages: nextPackages },
        "Package deleted successfully"
      );

      if (editorIndex === index) {
        startAdd();
      } else if (editorIndex !== null && editorIndex > index) {
        setEditorIndex(editorIndex - 1);
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert(error.message || "Failed to delete package");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cms-page">
        <div className="cms-page-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="cms-page">
      <div className="cms-page-header">
        <div>
          <h1>Packages</h1>
          <p>Add a new package or update an existing one from the list below.</p>
        </div>

        <button
          type="button"
          className="cms-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="cms-packages-main">
        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>Section Content</h2>
            <p>Edit the heading and description shown above the package tabs.</p>
          </div>

          <div className="cms-form-grid">
            <div className="cms-form-group">
              <label>Subtitle</label>
              <input
                type="text"
                value={data.subtitle || ""}
                onChange={(e) => handleSectionChange("subtitle", e.target.value)}
              />
            </div>

            <div className="cms-form-group">
              <label>Main Heading</label>
              <input
                type="text"
                value={data.mainTitle || ""}
                onChange={(e) => handleSectionChange("mainTitle", e.target.value)}
              />
            </div>

            <div className="cms-form-group cms-form-full">
              <label>Description</label>
              <textarea
                rows="4"
                value={data.description || ""}
                onChange={(e) =>
                  handleSectionChange("description", e.target.value)
                }
              />
            </div>
          </div>
        </div>

        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>{isEditing ? "Update Package" : "Add Package"}</h2>
            <p>
              {isEditing
                ? `Editing package ${editorIndex + 1}. Save changes to update it.`
                : "Fill the form and save to add a new package."}
            </p>
          </div>

          <div className="cms-editor-toolbar">
            <button type="button" className="cms-upload-btn" onClick={startAdd}>
              Add New
            </button>
          </div>

          <div className="cms-form-grid">
          <div className="cms-form-group">
            <label>Package Image</label>
            <div className="cms-upload-box">
              <div className="cms-upload-preview">
                {form.image ? (
                  <img
                    src={getMediaUrl(form.image)}
                    alt={form.title || "Package"}
                  />
                ) : (
                  <span>No image selected</span>
                )}
              </div>
              <div className="cms-upload-actions">
                <input
                  type="file"
                  id="package-image-editor"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="cms-file-input"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                />
                <label htmlFor="package-image-editor" className="cms-upload-btn">
                  Choose Image
                </label>
              </div>
            </div>
            <small>JPG, JPEG, PNG or WEBP.</small>
          </div>

          <div className="cms-form-group">
            <label>Tab Label</label>
            <input
              type="text"
              value={form.tabLabel || ""}
              onChange={(e) => handleFormChange("tabLabel", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Title</label>
            <input
              type="text"
              value={form.title || ""}
              onChange={(e) => handleFormChange("title", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Short Description</label>
            <textarea
              rows="4"
              value={form.shortDesc || ""}
              onChange={(e) => handleFormChange("shortDesc", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Image Alt Text</label>
            <input
              type="text"
              value={form.imageAlt || ""}
              onChange={(e) => handleFormChange("imageAlt", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Duration</label>
            <input
              type="text"
              value={form.duration || ""}
              onChange={(e) => handleFormChange("duration", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Capacity</label>
            <input
              type="text"
              value={form.capacity || ""}
              onChange={(e) => handleFormChange("capacity", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Inclusions</label>
            <textarea
              rows="5"
              value={(form.includes || []).join("\n")}
              onChange={(e) =>
                handleFormChange(
                  "includes",
                  e.target.value
                    .split("\n")
                    .map((item) => item.trim())
                    .filter(Boolean)
                )
              }
            />
            <small>One inclusion per line.</small>
          </div>

          <div className="cms-form-group">
            <label>Why Guests Love It</label>
            <textarea
              rows="4"
              value={form.whyLove || ""}
              onChange={(e) => handleFormChange("whyLove", e.target.value)}
            />
          </div>

          <div className="cms-form-group">
            <label>Base Price</label>
            <input
              type="text"
              value={form.basePrice || ""}
              onChange={(e) => handleFormChange("basePrice", e.target.value)}
            />
          </div>
          </div>

          <div className="cms-bottom-actions">
            <button
              type="button"
              className="cms-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        <div className="cms-section-card">
          <div className="cms-section-card-header">
            <h2>Existing Packages</h2>
            <p>Update fills the form above. Delete asks for confirmation first.</p>
          </div>

          {data.packages.length === 0 ? (
            <p className="cms-inquiries-empty">No packages added yet.</p>
          ) : (
            <div className="cms-item-list">
              {data.packages.map((pkg, index) => (
                <div
                  className={`cms-item-row${
                    editorIndex === index ? " is-active" : ""
                  }`}
                  key={index}
                >
                  <div className="cms-item-row-thumb">
                    {pkg.image ? (
                      <img
                        src={getMediaUrl(pkg.image)}
                        alt={pkg.title || "Package"}
                      />
                    ) : (
                      <span>No image</span>
                    )}
                  </div>
                  <div className="cms-item-row-info">
                    <strong>{pkg.title || `Package ${index + 1}`}</strong>
                    <span>{pkg.tabLabel || "No tab label"}</span>
                  </div>
                  <div className="cms-item-row-actions">
                    <button
                      type="button"
                      className="cms-upload-btn"
                      onClick={() => startEdit(index)}
                    >
                      Update
                    </button>
                    <button
                      type="button"
                      className="cms-delete-btn"
                      onClick={() => handleDelete(index)}
                      disabled={saving}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackagesCms;
